<template>
  <div class="kds-help-tooltip" :class="{ 'kds-help-tooltip--open': isOpen }">
    <button
      ref="triggerRef"
      type="button"
      class="kds-help-tooltip__trigger"
      :aria-expanded="isOpen"
      :aria-describedby="popoverId"
      :aria-label="title || 'Help'"
      @click="toggle"
      @keydown.escape="close"
      @blur="onBlur"
    >
      <KdsIcon name="circle-help" :size="16" />
    </button>

    <!-- Desktop popover (pointer: fine) -->
    <Teleport to="body">
      <div
        v-if="isOpen && !isCoarse"
        :id="popoverId"
        ref="popoverRef"
        role="tooltip"
        class="kds-help-tooltip__popover"
        :class="`kds-help-tooltip__popover--${resolvedPlacement}`"
        :style="popoverStyle"
        @mousedown.prevent
      >
        <p
          v-if="title"
          class="kds-help-tooltip__popover-title"
        >{{ title }}</p>
        <p class="kds-help-tooltip__popover-content">{{ content }}</p>
      </div>
    </Teleport>

    <!-- Mobile bottom-sheet (pointer: coarse) -->
    <q-dialog
      v-if="isCoarse"
      v-model="isOpen"
      position="bottom"
      flat
      :maximized="false"
    >
      <div class="kds-help-tooltip__sheet">
        <div class="kds-help-tooltip__sheet-header">
          <p v-if="title" class="kds-help-tooltip__sheet-title">{{ title }}</p>
          <button
            type="button"
            class="kds-help-tooltip__sheet-close"
            :aria-label="'Close'"
            @click="close"
          >
            <KdsIcon name="x" :size="16" />
          </button>
        </div>
        <p class="kds-help-tooltip__sheet-content">{{ content }}</p>
      </div>
    </q-dialog>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, useId } from 'vue';
import KdsIcon from '@/kds/primitives/KdsIcon.vue';

type Placement = 'top' | 'bottom';

interface Props {
  /** Help tooltip body text. */
  content: string;
  /** Optional title rendered above the content. */
  title?: string;
  /** Preferred popover placement on desktop; flips automatically when near viewport edge. */
  placement?: Placement;
}

const props = withDefaults(defineProps<Props>(), {
  title: undefined,
  placement: 'top'
});

const isOpen = ref(false);
const triggerRef = ref<HTMLButtonElement | null>(null);
const popoverRef = ref<HTMLDivElement | null>(null);

// Unique IDs for aria-describedby
const popoverId = useId ? useId() : `kds-help-tooltip-${Math.random().toString(36).slice(2)}`;

/** Detects touch/coarse pointer devices for bottom-sheet mode. */
const isCoarse = ref(false);
onMounted(() => {
  isCoarse.value = window.matchMedia('(pointer: coarse)').matches;
});

/** Resolved popover placement after flip check. */
const resolvedPlacement = ref<Placement>(props.placement);

type PopoverStyle = {
  top?: string;
  bottom?: string;
  left?: string;
  transform?: string;
  position: string;
};

const popoverStyle = ref<PopoverStyle>({ position: 'fixed' });

const GAP = 8;

/**
 * Computes the popover position relative to the trigger, with top/bottom flip.
 */
const updatePopoverPosition = async (): Promise<void> => {
  await nextTick();
  if (!triggerRef.value || !popoverRef.value) return;

  const triggerRect = triggerRef.value.getBoundingClientRect();
  const popoverEl = popoverRef.value;
  const popoverHeight = popoverEl.offsetHeight || 160;
  const viewportHeight = window.innerHeight;

  // Determine placement: flip if preferred side doesn't have enough space
  let placement = props.placement;
  if (placement === 'top' && triggerRect.top - GAP - popoverHeight < 0) {
    placement = 'bottom';
  } else if (placement === 'bottom' && triggerRect.bottom + GAP + popoverHeight > viewportHeight) {
    placement = 'top';
  }
  resolvedPlacement.value = placement;

  const centerX = triggerRect.left + triggerRect.width / 2;
  const popoverWidth = popoverEl.offsetWidth || 280;

  let left = centerX - popoverWidth / 2;
  // Clamp to viewport with 8px margin
  left = Math.max(8, Math.min(left, window.innerWidth - popoverWidth - 8));

  if (placement === 'top') {
    popoverStyle.value = {
      position: 'fixed',
      bottom: `${viewportHeight - triggerRect.top + GAP}px`,
      left: `${left}px`
    };
  } else {
    popoverStyle.value = {
      position: 'fixed',
      top: `${triggerRect.bottom + GAP}px`,
      left: `${left}px`
    };
  }
};

const toggle = (): void => {
  if (isOpen.value) {
    close();
  } else {
    open();
  }
};

const open = (): void => {
  isOpen.value = true;
  if (!isCoarse.value) {
    void updatePopoverPosition();
  }
};

const close = (): void => {
  isOpen.value = false;
};

/** Close on blur unless focus moves into popover (desktop). */
const onBlur = (event: FocusEvent): void => {
  if (isCoarse.value) return;
  const relatedTarget = event.relatedTarget as HTMLElement | null;
  if (popoverRef.value && relatedTarget && popoverRef.value.contains(relatedTarget)) return;
  close();
};

/** Close on click outside (desktop). */
const onDocumentClick = (event: MouseEvent): void => {
  if (isCoarse.value) return;
  const target = event.target as Node;
  if (
    triggerRef.value &&
    !triggerRef.value.contains(target) &&
    popoverRef.value &&
    !popoverRef.value.contains(target)
  ) {
    close();
  }
};

onMounted(() => {
  document.addEventListener('click', onDocumentClick, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick, true);
});

</script>

<style scoped lang="scss">
.kds-help-tooltip {
  display: inline-flex;
  align-items: center;

  &__trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    width: 20px;
    height: 20px;
    color: var(--stone);
    background: transparent;
    border: none;
    border-radius: var(--r-pill);
    cursor: pointer;
    transition: color var(--d-fast) var(--ease-out);

    &:hover {
      color: var(--ink);
    }

    &:focus-visible {
      outline: none;
      box-shadow: var(--shadow-focus);
    }
  }

  &--open &__trigger {
    color: var(--ink);
  }

  &__popover {
    position: fixed;
    z-index: 9999;
    width: 280px;
    max-width: calc(100vw - 16px);
    padding: var(--space-4);
    background: var(--paper);
    border: var(--border);
    border-radius: var(--r-4);
    box-shadow: var(--shadow-3);
  }

  &__popover-title {
    margin: 0 0 var(--space-2) 0;
    font-family: var(--font-ui);
    font-weight: var(--fw-semibold);
    font-size: var(--fs-body-sm);
    color: var(--ink);
  }

  &__popover-content {
    margin: 0;
    font-family: var(--font-ui);
    font-size: var(--fs-body-sm);
    line-height: var(--lh-body);
    color: var(--stone);
  }

  &__sheet {
    padding: var(--space-6);
    background: var(--paper);
    border-radius: var(--r-5) var(--r-5) 0 0;
  }

  &__sheet-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--space-4);
  }

  &__sheet-title {
    margin: 0;
    font-family: var(--font-ui);
    font-weight: var(--fw-semibold);
    font-size: var(--fs-body);
    color: var(--ink);
  }

  &__sheet-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-1);
    color: var(--stone);
    background: transparent;
    border: none;
    border-radius: var(--r-2);
    cursor: pointer;

    &:focus-visible {
      outline: none;
      box-shadow: var(--shadow-focus);
    }
  }

  &__sheet-content {
    margin: 0;
    font-family: var(--font-ui);
    font-size: var(--fs-body-sm);
    line-height: var(--lh-body);
    color: var(--stone);
  }
}
</style>
